import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useSubscription } from '@/hooks/useSubscription';

export function useMatters() {
  const { user } = useAuth();
  return useQuery({
    queryKey: ['matters', user?.id],
    enabled: !!user,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('matters')
        .select('*')
        .order('created_at', { ascending: false });
      if (error) throw error;
      return data;
    },
  });
}

export function useMatter(matterId: string | undefined) {
  return useQuery({
    queryKey: ['matters', matterId],
    enabled: !!matterId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('matters')
        .select('*')
        .eq('id', matterId!)
        .single();
      if (error) throw error;
      return data;
    },
  });
}

export function useMatterByNumber(matterNumber: string | undefined) {
  return useQuery({
    queryKey: ['matters', 'number', matterNumber],
    enabled: !!matterNumber,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('matters')
        .select('*')
        .eq('matter_number', matterNumber!)
        .single();
      if (error) throw error;
      return data;
    },
  });
}

export function useCreateMatter() {
  const queryClient = useQueryClient();
  const { entitlements, currentPlan } = useSubscription();
  return useMutation({
    mutationFn: async (matter: {
      deal_id: string;
      lawyer_id: string;
      client_id?: string | null;
      agent_id?: string | null;
      title: string;
      property_address?: string | null;
      property_value?: number | null;
      estimated_completion?: string | null;
    }) => {
      if (entitlements.activeMatterLimit !== null) {
        const { data: existingMatters, error: existingError } = await supabase
          .from('matters')
          .select('id, status');
        if (existingError) throw existingError;

        const activeStatuses = new Set(['completed', 'closed', 'cancelled', 'archived']);
        const activeCount = (existingMatters || []).filter(
          (existingMatter) => !activeStatuses.has((existingMatter.status || '').toLowerCase()),
        ).length;

        if (activeCount >= entitlements.activeMatterLimit) {
          throw new Error(
            `${currentPlan.name} supports up to ${entitlements.activeMatterLimit} active matters. Upgrade your plan to create more.`,
          );
        }
      }

      const { data, error } = await supabase
        .from('matters')
        .insert(matter)
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['matters'] });
    },
  });
}

export function useMatterStages(matterId: string | undefined) {
  const { profile } = useAuth();
  const isLawyer = profile?.role === 'lawyer';

  return useQuery({
    queryKey: ['matter_stages', matterId],
    enabled: !!matterId,
    queryFn: async () => {
      if (isLawyer) {
        // Lawyers get full access including internal_notes via RLS
        const { data, error } = await supabase
          .from('matter_stages')
          .select('*')
          .eq('matter_id', matterId!)
          .order('sort_order', { ascending: true });
        if (error) throw error;
        return data;
      } else {
        // Clients/agents use security definer function (no internal_notes)
        const { data, error } = await supabase.rpc('get_matter_stages_for_client', {
          p_matter_id: matterId!,
        });
        if (error) throw error;
        return data;
      }
    },
  });
}

export function useUpdateMatterStage() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...updates }: { id: string; status?: string; started_at?: string; completed_at?: string; internal_notes?: string; client_explanation?: string }) => {
      const { data, error } = await supabase
        .from('matter_stages')
        .update(updates)
        .eq('id', id)
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['matter_stages', data.matter_id] });
      queryClient.invalidateQueries({ queryKey: ['matters'] });
    },
  });
}
